import { useEffect, useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { money } from "../format.js";
import {
  AuthImage, Button, Card, Check, ErrorPanel, Field, Fields, Grid, Input, Loading, Row, Select, Sep, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";
import { FlowWordmark } from "../components/Logo.jsx";

/**
 * The organisation: its letterhead, its invoicing defaults, its automation.
 *
 * The letterhead fields are not decorative — the PDF prints them verbatim at the
 * top of every invoice, so the preview beside the form lays them out in the same
 * order the renderer does (netlify/lib/pdf.mjs → issuerLines) and updates as you
 * type. Seeing "Operating as …" appear is how you learn what that field is for.
 */

// pdf-lib can embed PNG and JPEG and nothing else. Checking here rather than
// letting the server say no means the user learns the rule before the upload.
const LOGO_TYPES = ["image/png", "image/jpeg"];
const MIN_LOGO_WIDTH = 200;

const CURRENCIES = [
  { code: "CAD", symbol: "$" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "AUD", symbol: "$" },
];
const TAX_LABELS = ["HST", "GST", "VAT", "Sales tax", "PST", "QST"];
const TERMS = [0, 15, 30, 45, 60];

const num = (v, fallback = 0) => (v === "" || v === null || v === undefined ? fallback : Number(v) || 0);
const lines = (s) => String(s || "").split("\n").map((l) => l.trim()).filter(Boolean);

/** Reads the intrinsic size of a picked image without a round-trip to the server. */
function measureImage(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ url, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

const THUMB = {
  width: 46, height: 46, borderRadius: 9, objectFit: "contain",
  border: "1px solid var(--line)", background: "var(--panel-2)", flex: "none",
};
const PREVIEW_LOGO = { maxWidth: 130, maxHeight: 42, objectFit: "contain", display: "block" };

export default function Settings() {
  const { reloadApp } = useApp();
  const toast = useToast();
  const state = useAsync(() => api.get("organizations"), []);
  const invoices = useAsync(() => api.get("invoices?status=all"), []);

  const [form, setForm] = useState(null);
  const [logoKey, setLogoKey] = useState(null);
  const [localLogo, setLocalLogo] = useState(null); // { url, width, height } while it is fresh
  const [logoNote, setLogoNote] = useState(null);
  // null = not yet known, true = the stored logo loaded, false = it 404'd and the
  // preview is showing the built-in wordmark instead.
  const [logoResolved, setLogoResolved] = useState(null);
  const [pickerKey, setPickerKey] = useState(0); // remounts the file input so re-picking fires
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);

  // FreshBooks import. The report is a dry run; nothing is written until Import.
  const [clientsFile, setClientsFile] = useState(null);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [report, setReport] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [wipe, setWipe] = useState(false);
  const [confirmWipe, setConfirmWipe] = useState("");

  // Seed the editable copy once, so typing isn't fighting the fetched data.
  useEffect(() => {
    if (!state.data) return;
    setForm({ ...state.data });
    setLogoKey(state.data.logo_blob_key || null);
    setLogoResolved(null); // a different key has to prove itself again
  }, [state.data]);

  // Object URLs are a leak if we never hand them back.
  useEffect(() => () => localLogo?.url && URL.revokeObjectURL(localLogo.url), [localLogo]);

  if (!form)
    return state.error ? (
      <Wrap>
        <ErrorPanel error={state.error} onRetry={state.reload} />
      </Wrap>
    ) : (
      <Loading label="Loading your business…" />
    );

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const terms = TERMS.includes(Number(form.default_terms_days))
    ? TERMS
    : [...TERMS, Number(form.default_terms_days)].sort((a, b) => a - b);
  const currencies = CURRENCIES.some((c) => c.code === form.currency_code)
    ? CURRENCIES
    : [{ code: form.currency_code, symbol: form.currency_symbol || "$" }, ...CURRENCIES];
  const taxLabels = form.tax_label && !TAX_LABELS.includes(form.tax_label)
    ? [form.tax_label, ...TAX_LABELS]
    : TAX_LABELS;

  // A just-picked file is already a local object URL and can go straight into src.
  // A stored logo has to be fetched with the auth header — see AuthImage.
  const localSrc = localLogo?.url || null;
  const logoPath = !localSrc && logoKey ? api.filePath(logoKey) : null;
  const hasLogo = !!(localSrc || logoPath);
  const latestInvoice = invoices.data?.list?.[0] || null;

  function pickCurrency(code) {
    const c = currencies.find((x) => x.code === code);
    setForm((f) => ({ ...f, currency_code: code, currency_symbol: c ? c.symbol : f.currency_symbol }));
  }

  async function save() {
    setError(null);
    if (!String(form.name || "").trim()) {
      setError(new Error("What is the business called? A name is required."));
      return;
    }
    setSaving(true);
    try {
      const saved = await api.put("organizations", {
        name: String(form.name || "").trim(),
        legal_name: form.legal_name || "",
        trading_as: form.trading_as || "",
        email: form.email || "",
        phone: form.phone || "",
        website: form.website || "",
        address: form.address || "",
        tax_number: form.tax_number || "",
        tax_label: form.tax_label || "",
        tax_rate: num(form.tax_rate),
        currency_code: form.currency_code || "CAD",
        currency_symbol: form.currency_symbol || "$",
        default_terms_days: num(form.default_terms_days, 30),
        invoice_prefix: form.invoice_prefix || "",
        estimate_prefix: form.estimate_prefix || "",
        footer_note: form.footer_note || "",
        payment_instructions: form.payment_instructions || "",
        remind_before_days: num(form.remind_before_days, 3),
        remind_after_days: num(form.remind_after_days, 1),
        remind_final_days: num(form.remind_final_days, 7),
        late_fee_percent: num(form.late_fee_percent),
        notify_on_view: !!form.notify_on_view,
        notify_on_payment: !!form.notify_on_payment,
        budget_alert_pct: num(form.budget_alert_pct, 85),
      });
      setForm({ ...saved });
      setLogoKey(saved.logo_blob_key || null);
      toast.push("Saved — new invoices will use this letterhead");
      reloadApp();
    } catch (e) {
      setError(e);
      toast.push(e.message || "Could not save your changes", "err");
    } finally {
      setSaving(false);
    }
  }

  /** The logo is stored the moment it uploads — it isn't part of Save changes. */
  async function uploadLogo(file) {
    setPickerKey((k) => k + 1);
    if (!file) return;
    setError(null);
    setLogoNote(null);

    const type = String(file.type || "").toLowerCase().split(";")[0];
    if (!LOGO_TYPES.includes(type)) {
      toast.push(
        `That file is ${file.type || "of an unrecognised type"}. A logo has to be a PNG or a JPEG — ` +
          `those are the only two formats that can be embedded in an invoice PDF. SVGs and WebP won't ` +
          `print. Re-export it as .png (best for logos with flat colour) and try again.`,
        "err"
      );
      return;
    }

    const dims = await measureImage(file);
    if (dims) {
      setLocalLogo(dims);
      if (Number(dims.width) < MIN_LOGO_WIDTH)
        setLogoNote(
          `Heads up: this image is only ${Number(dims.width)}px wide. Invoices print it around 150pt ` +
            `across, so anything under ${MIN_LOGO_WIDTH}px will look soft on paper. A version at ` +
            `400px or wider will hold up.`
        );
    }

    setUploading(true);
    try {
      const res = await api.upload("organizations/logo", file);
      setLogoKey(res.logo_blob_key);
      setLogoResolved(null);

      // The upload also heals documents whose frozen letterhead pointed at an image
      // that had gone missing. Worth saying out loud — it rewrites those snapshots,
      // and it's the thing the user was trying to fix by re-uploading.
      const healed = (res.repaired_invoices || 0) + (res.repaired_estimates || 0);
      if (healed) {
        toast.push(
          `Logo updated, and restored on ${res.repaired_invoices} invoice(s) and ` +
            `${res.repaired_estimates} estimate(s) that had lost it`
        );
      } else {
        toast.push("Logo updated");
      }
      reloadApp();
    } catch (e) {
      setError(e);
      setLocalLogo(null);
    } finally {
      setUploading(false);
    }
  }

  async function removeLogo() {
    setError(null);
    setRemoving(true);
    try {
      await api.del("organizations/logo");
      setLogoKey(null);
      setLocalLogo(null);
      setLogoNote(null);
      toast.push("Logo removed — documents fall back to the built-in wordmark");
      reloadApp();
    } catch (e) {
      setError(e);
      toast.push(e.message || "Could not remove the logo", "err");
    } finally {
      setRemoving(false);
    }
  }

  /* ------------------------------------------------------- FreshBooks import */

  const sym = form.currency_symbol || "$";
  const fatal = report?.problems || [];
  const canImport = !!report && !fatal.length && (!wipe || confirmWipe.trim() === "REPLACE");

  /**
   * The endpoint wants one optional `clients` file and one `invoices` entry per
   * file — FreshBooks splits exports by date range, so there are usually several.
   */
  function importForm() {
    const fd = new FormData();
    if (clientsFile) fd.append("clients", clientsFile);
    for (const f of invoiceFiles) fd.append("invoices", f);
    return fd;
  }

  /** Any change to the selection invalidates a report that was built from it. */
  function pickFiles(which, files) {
    setReport(null);
    setImportResult(null);
    setImportError(null);
    if (which === "clients") setClientsFile(files?.[0] || null);
    else setInvoiceFiles(files ? Array.from(files) : []);
  }

  async function previewImport() {
    setImportError(null);
    setImportResult(null);
    setReport(null);
    if (!invoiceFiles.length) {
      setImportError(new Error("Attach at least one FreshBooks invoice export (.csv) first."));
      return;
    }
    setPreviewing(true);
    try {
      setReport(await api.uploadForm("import/freshbooks", importForm()));
    } catch (e) {
      setImportError(e);
    } finally {
      setPreviewing(false);
    }
  }

  async function runImport() {
    setImportError(null);
    if (!canImport) return;
    setImporting(true);
    try {
      const path = `import/freshbooks?commit=1${wipe ? "&wipe=1" : ""}`;
      const res = await api.uploadForm(path, importForm());
      setReport(res);
      setImportResult(res.result || null);
      setWipe(false);
      setConfirmWipe("");
      toast.push(`Imported ${Number(res.result?.imported) || 0} invoices from FreshBooks`);
      reloadApp();
    } catch (e) {
      setImportError(e);
      toast.push(e.message || "The import did not run", "err");
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <TopBar crumb="Manage" title="Settings">
        <Button kind="pri" busy={saving} onClick={save}>
          Save changes
        </Button>
      </TopBar>

      <Wrap>
        {error && <ErrorPanel error={error} />}

        <Grid cols="1fr 1fr" style={{ alignItems: "start" }}>
          <Card title="Letterhead">
            <Fields>
              <Field
                label="Logo"
                hint="PNG or JPEG only, under 2 MB. Saved as soon as you pick a file — no need to press Save."
              >
                <Row style={{ alignItems: "flex-start" }}>
                  {hasLogo ? (
                    localSrc ? (
                      <img src={localSrc} alt="Your logo" style={THUMB} />
                    ) : (
                      <AuthImage path={logoPath} alt="Your logo" style={THUMB} fallback={
                        <span className="tiny muted nowrap" style={{ paddingTop: 9 }}>Couldn't load</span>
                      } />
                    )
                  ) : (
                    <span className="tiny muted nowrap" style={{ paddingTop: 9 }}>
                      None yet
                    </span>
                  )}
                  <div style={{ flex: 1 }}>
                    <Input
                      key={pickerKey}
                      type="file"
                      accept="image/png,image/jpeg"
                      disabled={uploading}
                      onChange={(e) => uploadLogo(e.target.files?.[0])}
                    />
                    <Row style={{ marginTop: 6 }}>
                      {uploading && <span className="tiny muted nowrap">Uploading…</span>}
                      {logoKey && !uploading && (
                        <Button sm kind="dgr" busy={removing} onClick={removeLogo}>
                          Remove
                        </Button>
                      )}
                    </Row>
                  </div>
                </Row>
              </Field>

              {logoNote && (
                <div className="ribbon w" style={{ marginBottom: 0 }}>
                  {logoNote}
                </div>
              )}

              <Field label="Business name" hint="The name clients know you by. Heads every invoice.">
                <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
              </Field>

              <Fields cols={2}>
                <Field label="Registered legal name" hint="Printed underneath only when it differs.">
                  <Input value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} />
                </Field>
                <Field label="Operating as" hint="A trading name, if you bill under one.">
                  <Input value={form.trading_as || ""} onChange={(e) => set("trading_as", e.target.value)} />
                </Field>
              </Fields>

              <Field label="Address" hint="One line per row — printed exactly as you type it.">
                <Textarea rows={3} value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
              </Field>

              <Fields cols={2}>
                <Field label="Billing email">
                  <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
                </Field>
              </Fields>

              <Field label="Website">
                <Input
                  value={form.website || ""}
                  placeholder="northwind.studio"
                  onChange={(e) => set("website", e.target.value)}
                />
              </Field>

              <Field
                label="Tax registration number"
                hint={`Prints as "${form.tax_label || "Tax"} No. …". In Canada a GST/HST registrant must show it on any invoice of $30 or more, or the client can't claim the input tax credit. The label itself lives under Invoice defaults.`}
              >
                <Input value={form.tax_number || ""} onChange={(e) => set("tax_number", e.target.value)} />
              </Field>
            </Fields>
          </Card>

          <div style={{ display: "grid", gap: 14 }}>
            <Card title="How it prints" right={<span className="tiny muted">Live</span>}>
              <LetterheadPreview
                form={form}
                localSrc={localSrc}
                logoPath={logoPath}
                onLogoResolved={setLogoResolved}
              />

              {/* Names the mark being shown. The built-in wordmark is the same artwork
                  as most uploaded logos will be, so "is this my file or the fallback?"
                  is genuinely impossible to answer by eye — it has to be stated. */}
              {localSrc ? (
                <div className="tiny muted" style={{ marginTop: 8 }}>
                  Showing the file you just picked.
                </div>
              ) : !logoPath ? (
                <div className="tiny muted" style={{ marginTop: 8 }}>
                  No logo uploaded — documents print the built-in wordmark.
                </div>
              ) : logoResolved === false ? (
                <div className="ribbon w" style={{ marginTop: 10, marginBottom: 0 }}>
                  Your uploaded logo could not be loaded, so this is the built-in wordmark. Pick the file
                  again above — that re-uploads it and repairs any invoice still pointing at the missing
                  image.
                </div>
              ) : logoResolved ? (
                <div className="tiny muted" style={{ marginTop: 8 }}>
                  Showing your uploaded logo.
                </div>
              ) : null}

              <Sep />

              <Button
                disabled={!latestInvoice}
                onClick={() => latestInvoice && api.openBlob(api.invoicePdfPath(latestInvoice.id)).catch((e) => toast.push(e.message, "err"))}
              >
                ⤓ Preview invoice PDF
              </Button>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                {invoices.loading
                  ? "Looking for an invoice to preview…"
                  : invoices.error
                  ? "Couldn't check your invoices, so the preview is unavailable right now."
                  : latestInvoice
                  ? `Opens ${latestInvoice.number} in a new tab — your most recent invoice, rendered by the real PDF engine. Sent invoices show the letterhead they were sent with; drafts re-render, so a draft is the honest test of changes above.`
                  : "You have no invoices yet, so there is nothing to render. Issue one and this becomes a full-page preview."}
              </div>
            </Card>

            <Card title="What changes, and when">
              <div className="sm dim">
                Every invoice and estimate snapshots its issuer — letterhead, logo, tax label, rate and
                currency symbol — at the moment it is sent. Editing anything on this page affects{" "}
                <b>future documents only</b>. An invoice you have already sent will keep reprinting with
                the details it carried then, which is exactly what you want when a client queries a
                two-year-old bill or an auditor asks.
              </div>
            </Card>
          </div>
        </Grid>

        <Grid cols="1fr 1fr" className="mt2" style={{ alignItems: "start" }}>
          <Card title="Invoice defaults">
            <Fields>
              <Fields cols={2}>
                <Field label="Invoice prefix">
                  <Input value={form.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)} />
                </Field>
                <Field label="Estimate prefix">
                  <Input value={form.estimate_prefix || ""} onChange={(e) => set("estimate_prefix", e.target.value)} />
                </Field>
              </Fields>

              <Fields cols={2}>
                <Field label="Currency">
                  <Select value={form.currency_code || "CAD"} onChange={(e) => pickCurrency(e.target.value)}>
                    {currencies.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Symbol" hint={`Amounts read ${money(1234.5, form.currency_symbol || "$")}.`}>
                  <Input value={form.currency_symbol || ""} onChange={(e) => set("currency_symbol", e.target.value)} />
                </Field>
              </Fields>

              <Fields cols={2}>
                <Field label="Tax label">
                  <Select value={form.tax_label || ""} onChange={(e) => set("tax_label", e.target.value)}>
                    <option value="">None — I don't charge tax</option>
                    {taxLabels.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Tax rate %">
                  <Input
                    className="i num"
                    inputMode="decimal"
                    value={form.tax_rate ?? ""}
                    onChange={(e) => set("tax_rate", e.target.value)}
                  />
                </Field>
              </Fields>

              <Field label="Default payment terms">
                <Select
                  value={Number(form.default_terms_days) || 0}
                  onChange={(e) => set("default_terms_days", e.target.value)}
                >
                  {terms.map((t) => (
                    <option key={t} value={t}>
                      {t ? `Net ${t}` : "Due on receipt"}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Footer note" hint="A closing line under the totals — a thank-you, or your terms.">
                <Textarea rows={2} value={form.footer_note || ""} onChange={(e) => set("footer_note", e.target.value)} />
              </Field>

              <Field
                label="Payment instructions"
                hint="Printed in the footer of every invoice — bank details, cheque payee, wire instructions."
              >
                <Textarea
                  rows={4}
                  value={form.payment_instructions || ""}
                  onChange={(e) => set("payment_instructions", e.target.value)}
                />
              </Field>

              <Sep />
              <div className="tiny muted">
                The next invoice will be numbered{" "}
                <span className="num">
                  {form.invoice_prefix}
                  {form.next_invoice_seq}
                </span>{" "}
                and the next estimate{" "}
                <span className="num">
                  {form.estimate_prefix}
                  {form.next_estimate_seq}
                </span>
                . Numbering is per business and never reused.
              </div>
            </Fields>
          </Card>

          <div style={{ display: "grid", gap: 14 }}>
            <Card title="Reminders">
              <Fields>
                <Fields cols={3}>
                  <Field label="Days before due">
                    <Input
                      className="i num"
                      inputMode="numeric"
                      value={form.remind_before_days ?? ""}
                      onChange={(e) => set("remind_before_days", e.target.value)}
                    />
                  </Field>
                  <Field label="Days after due">
                    <Input
                      className="i num"
                      inputMode="numeric"
                      value={form.remind_after_days ?? ""}
                      onChange={(e) => set("remind_after_days", e.target.value)}
                    />
                  </Field>
                  <Field label="Final notice, days after">
                    <Input
                      className="i num"
                      inputMode="numeric"
                      value={form.remind_final_days ?? ""}
                      onChange={(e) => set("remind_final_days", e.target.value)}
                    />
                  </Field>
                </Fields>
                <Field label="Late fee % per month" hint="Set to 0 to never mention a late fee.">
                  <Input
                    className="i num"
                    inputMode="decimal"
                    value={form.late_fee_percent ?? ""}
                    onChange={(e) => set("late_fee_percent", e.target.value)}
                  />
                </Field>
                <div className="tiny muted">
                  A reminder email goes out on each of these days for any invoice that is still open. At
                  most one reminder per invoice per run.
                </div>
              </Fields>
            </Card>

            <Card title="Notifications">
              <Fields>
                <Check
                  label="Email me when a client views an invoice"
                  checked={!!form.notify_on_view}
                  onChange={(v) => set("notify_on_view", v)}
                />
                <Check
                  label="Email me when a payment is received"
                  checked={!!form.notify_on_payment}
                  onChange={(v) => set("notify_on_payment", v)}
                />
                <Field label="Alert me when a project passes this % of budget">
                  <Input
                    className="i num"
                    inputMode="numeric"
                    value={form.budget_alert_pct ?? ""}
                    onChange={(e) => set("budget_alert_pct", e.target.value)}
                  />
                </Field>
                <div className="tiny muted">
                  Notifications go to {form.email || "your billing email"} — set it under Letterhead.
                </div>
              </Fields>
            </Card>
          </div>
        </Grid>

        <Card title="Import from FreshBooks" className="mt2">
          <Grid cols="1fr 1fr" style={{ alignItems: "start" }}>
            <Fields>
              <div className="sm dim">
                Bring your history across. Export from FreshBooks as CSV, attach the files below and
                preview first — a preview reads the files and reports what would happen without writing
                anything.
              </div>

              <Field
                label="Client list"
                hint="clients.csv — optional. Without it, clients are created from the names on the invoices only."
              >
                <Input type="file" accept=".csv" onChange={(e) => pickFiles("clients", e.target.files)} />
                <div className="tiny muted" style={{ marginTop: 4 }}>
                  {clientsFile ? clientsFile.name : "No file chosen"}
                </div>
              </Field>

              <Field
                label="Invoice exports"
                hint="FreshBooks splits an export by date range, so select every file — hold ⌘ or Ctrl to pick more than one."
              >
                <Input type="file" accept=".csv" multiple onChange={(e) => pickFiles("invoices", e.target.files)} />
                <div className="tiny muted" style={{ marginTop: 4 }}>
                  {invoiceFiles.length
                    ? invoiceFiles.map((f) => f.name).join(", ")
                    : "No files chosen"}
                </div>
              </Field>

              <Sep />

              <Check
                label="Replace existing records first"
                checked={wipe}
                onChange={(v) => {
                  setWipe(v);
                  setConfirmWipe("");
                }}
              />
              <div className="ribbon w" style={{ marginBottom: 0 }}>
                This <b>deletes the clients, invoices, estimates, payments, projects, time entries and
                expenses</b> already recorded for {form.name || "this business"} before importing. It cannot
                be undone. Leave it unticked to add the imported invoices alongside what you have — an
                invoice number that already exists is skipped rather than duplicated.
              </div>

              {wipe && (
                <Field label="Type REPLACE to confirm" hint="Required while the replace box is ticked.">
                  <Input
                    value={confirmWipe}
                    placeholder="REPLACE"
                    onChange={(e) => setConfirmWipe(e.target.value)}
                  />
                </Field>
              )}

              <Row>
                <Button busy={previewing} disabled={importing || !invoiceFiles.length} onClick={previewImport}>
                  Preview import
                </Button>
                <Button kind="pri" busy={importing} disabled={previewing || !canImport} onClick={runImport}>
                  Import
                </Button>
              </Row>
              <div className="tiny muted">
                {fatal.length
                  ? "The figures don't reconcile, so importing is blocked — the server refuses this export too."
                  : !report
                  ? "Preview first. Import stays disabled until a preview comes back clean."
                  : wipe && confirmWipe.trim() !== "REPLACE"
                  ? "Type REPLACE above to unlock the import."
                  : "Ready to import."}
              </div>
            </Fields>

            <div style={{ display: "grid", gap: 14 }}>
              {importError && <ErrorPanel error={importError} />}

              {!report && !importError && (
                <div className="sm muted">
                  Nothing previewed yet. The report appears here: totals, the date range, the clients that
                  would be created, and the number your next new invoice would take.
                </div>
              )}

              {report && (
                <>
                  {!!fatal.length && (
                    <ErrorPanel
                      error={new Error(
                        `The arithmetic doesn't reconcile, so nothing will be imported — ${fatal.join(" · ")}`
                      )}
                    />
                  )}

                  <div>
                    <div className="row">
                      <span className="sm dim">Total billed</span>
                      <div className="grow" />
                      <b className="num">{money(Number(report.totals?.billed), sym)}</b>
                    </div>
                    <Sep />
                    <div className="sm" style={{ display: "grid", gap: 3, whiteSpace: "pre-wrap" }}>
                      {(report.summary || []).map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  </div>

                  {report.numbering?.example && (
                    <div className="sm dim">
                      After importing, your next new invoice will be{" "}
                      <span className="num">{report.numbering.example}</span>.
                    </div>
                  )}

                  <div>
                    <div className="sm dim">
                      {(report.clients || []).length} client
                      {(report.clients || []).length === 1 ? "" : "s"} will be created:
                    </div>
                    <div className="tiny muted" style={{ marginTop: 4 }}>
                      {(report.clients || []).join(", ") || "None — no client names in the export."}
                    </div>
                  </div>

                  {!!(report.notes || []).length && (
                    <div>
                      <div className="sm dim">Worth knowing</div>
                      <ul className="tiny muted" style={{ margin: "4px 0 0", paddingLeft: 18, lineHeight: 1.7 }}>
                        {report.notes.map((n, i) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!!(report.rounded || []).length && (
                    <div>
                      <div className="sm dim">Rounded to the nearest cent</div>
                      <div className="tiny muted" style={{ marginTop: 4 }}>
                        FreshBooks keeps totals to four decimals, so these invoices carried fractional
                        cents. Amounts here are stored to the cent, so each was rounded to the nearest one.
                      </div>
                      <ul className="tiny muted" style={{ margin: "4px 0 0", paddingLeft: 18, lineHeight: 1.7 }}>
                        {report.rounded.map((r, i) => (
                          <li key={i} className="num">
                            {r.number}: {Number(r.from).toFixed(4)} → {Number(r.to).toFixed(2)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importResult && (
                    <div className="ribbon">
                      Imported <b>{Number(importResult.imported)}</b> invoices and{" "}
                      <b>{Number(importResult.clients_added)}</b> clients.{" "}
                      <b>{Number(importResult.skipped)}</b> skipped as already present. Billed total now{" "}
                      <b className="num">{money(Number(importResult.after?.billed), sym)}</b>; your next new
                      invoice is <span className="num">{importResult.next_number}</span>.
                    </div>
                  )}
                </>
              )}
            </div>
          </Grid>
        </Card>

        <Row className="mt2" style={{ justifyContent: "flex-end" }}>
          <Button kind="pri" busy={saving} onClick={save}>
            Save changes
          </Button>
        </Row>
      </Wrap>
    </>
  );
}

/**
 * The invoice header as the PDF renderer builds it: logo (or the Flow lockup when
 * there isn't one), name, legal name when it differs, "Operating as …", address
 * lines, email · phone, website, then the tax registration.
 */
function LetterheadPreview({ form, localSrc, logoPath, onLogoResolved }) {
  const name = String(form.name || "").trim();
  const legal = String(form.legal_name || "").trim();
  const tradingAs = String(form.trading_as || "").trim();
  const contact = [String(form.email || "").trim(), String(form.phone || "").trim()]
    .filter(Boolean)
    .join(" · ");
  const website = String(form.website || "").trim();
  const taxNumber = String(form.tax_number || "").trim();

  return (
    <div className="doc compact" style={{ padding: 22, maxWidth: "none", boxShadow: "none" }}>
      <div className="dh">
        <div style={{ maxWidth: 260 }}>
          {localSrc ? (
            <img src={localSrc} alt="" style={PREVIEW_LOGO} />
          ) : logoPath ? (
            <AuthImage
              path={logoPath}
              style={PREVIEW_LOGO}
              fallback={<FlowWordmark height={22} tone="light" />}
              onResolved={onLogoResolved}
            />
          ) : (
            /* No logo uploaded: the PDF falls back to the wordmark, so the preview does too. */
            <FlowWordmark height={22} tone="light" />
          )}

          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{name || "Your business name"}</div>
            <div className="tiny muted" style={{ lineHeight: 1.65, marginTop: 2 }}>
              {legal && legal !== name && <div>{legal}</div>}
              {tradingAs && <div>Operating as {tradingAs}</div>}
              {lines(form.address).map((l, i) => (
                <div key={i}>{l}</div>
              ))}
              {contact && <div>{contact}</div>}
              {website && <div>{website}</div>}
              {taxNumber && (
                <div style={{ fontWeight: 650, marginTop: 3 }}>
                  {form.tax_label || "Tax"} No. {taxNumber}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="right">
          <h1 style={{ fontSize: 22 }}>Invoice</h1>
          <div className="tiny dim num">
            {form.invoice_prefix}
            {form.next_invoice_seq}
          </div>
        </div>
      </div>
    </div>
  );
}
